import { create } from 'zustand';
import { User, Session } from '@supabase/supabase-js';
import { supabase, getSupabaseConfig } from '../lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setError: (error: string | null) => void;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: true,
  error: null,
  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setError: (error) => set({ error }),
  signOut: async () => {
    try {
      // First clear the local state
      set({ user: null, session: null, error: null });
      
      // Clear local storage to ensure all auth data is removed
      window.localStorage.removeItem('supabase.auth.token');
      
      // Then attempt to sign out from Supabase
      await supabase.auth.signOut();
    } catch (error) {
      console.warn('Error during sign out:', error);
      // Still keep the user signed out locally even if there's an error
      set({ user: null, session: null });
    }
  },
  initialize: async () => {
    try {
      set({ loading: true, error: null });

      // Check if Supabase is properly configured
      const config = getSupabaseConfig();
      if (!config.hasValidKey || !config.url.startsWith('https://')) {
        console.warn('Supabase configuration is incomplete or invalid:', {
          hasValidUrl: config.url.startsWith('https://'),
          hasValidKey: config.hasValidKey
        });
        
        set({
          loading: false,
          error: 'Supabase configuration is incomplete. Please check your environment variables.',
          user: null,
          session: null
        });
        return;
      }
      
      // Get initial session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('Session error:', sessionError);
        // Don't throw on session errors, just log and continue
        console.warn('Session initialization failed, continuing without session');
      }

      // Set initial state
      set({ 
        session,
        user: session?.user ?? null,
        loading: false,
        error: null
      });

      // User profile is now automatically created by database trigger
      // No need for manual profile creation here
    } catch (error: any) {
      console.error('Error initializing auth:', error);
      const errorMessage = error.message || 'Failed to initialize authentication';
      
      // Check if it's a connection error
      const isConnectionError = 
        error.message?.includes('fetch failed') || 
        error.message?.includes('network') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('timeout');
      
      set({ 
        error: isConnectionError 
          ? 'Unable to connect to Supabase. Please check your internet connection and Supabase configuration.' 
          : errorMessage,
        loading: false,
        user: null,
        session: null
      });
    }
  }
}));